import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import useOutsideClick from "src/hooks/useOutsideClick";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { GET_WORK_SESSIONS, GET_EMPLOYEE } from "src/constants/queries";
import { EMPLOYEE_CLOCK_OUT } from "src/constants/mutations";
import { failureToast, successToast } from "src/utils/toasts";
import { ClockOutConfirmModal } from "./ClockOutConfirmModal";
import { ReportDateRangePicker } from "src/features/Reports/components/ReportDateRangePicker";
import { getReportDateRange, type ReportDatePreset } from "src/features/Reports/utils/reportDateRange";

const PAGE_SIZE = 15;

/** Build page list for pagination: e.g. [1, 2, 3, 4] or [1, 2, 'ellipsis', 5] */
function getPaginationPages(currentPage: number, totalPages: number): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis", totalPages];
  }
  if (currentPage >= totalPages - 3) {
    return [1, "ellipsis", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [
    1,
    "ellipsis",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "ellipsis",
    totalPages,
  ];
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatTime(at: number | string | null | undefined): string {
  if (at == null) return "—";
  const ms = typeof at === "string" ? new Date(at).getTime() : at;
  if (Number.isNaN(ms)) return "—";
  const date = new Date(ms);
  return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: true });
}

function formatDate(at: number | string | null | undefined): string {
  if (at == null) return "—";
  const ms = typeof at === "string" ? new Date(at).getTime() : at;
  if (Number.isNaN(ms)) return "—";
  return new Date(ms).toLocaleDateString();
}

function hoursBetween(clockInAt: number | string | null | undefined, clockOutAt: number | string | null | undefined): number | null {
  if (clockInAt == null || clockOutAt == null) return null;
  const inMs = typeof clockInAt === "string" ? new Date(clockInAt).getTime() : clockInAt;
  const outMs = typeof clockOutAt === "string" ? new Date(clockOutAt).getTime() : clockOutAt;
  const hours = (outMs - inMs) / (1000 * 60 * 60);
  return hours < 0 ? null : hours;
}

function formatHours(hours: number | null): string {
  if (hours == null) return "—";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function resolveCreatedByUpdatedBy(
  value: string | null | undefined,
  employeeMap: Map<string, string>
): string {
  if (value == null || value === "") return "—";
  const trimmed = value.trim();
  if (UUID_REGEX.test(trimmed)) {
    const name = employeeMap.get(trimmed);
    if (name) return name;
    return "System";
  }
  return trimmed;
}

interface WorkSessionNode {
  id?: number | string;
  clockInAt?: number | string | null;
  clockOutAt?: number | string | null;
  notes?: string | null;
  storeCorrelationId?: string;
  userCorrelationId?: string;
  workDate?: string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
}

interface EmployeeOption {
  correlationId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export function TimecardsTab() {
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const storeCorrelationId = hasRestaurant?.correlationId;

  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last7");
  const [customFrom, setCustomFrom] = useState<string | null>(null);
  const [customTo, setCustomTo] = useState<string | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | "all">("all");

  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [sessions, setSessions] = useState<WorkSessionNode[]>([]);
  const [pageInfo, setPageInfo] = useState<{
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor: string | null;
    endCursor: string | null;
    totalNumberOfWorkSessions?: number;
  }>({ hasNextPage: false, hasPreviousPage: false, startCursor: null, endCursor: null });
  const [after, setAfter] = useState<string | null>(null);
  const [before, setBefore] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [cursorCache, setCursorCache] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [clockOutTarget, setClockOutTarget] = useState<{ userCorrelationId: string; employeeName: string } | null>(null);
  const [clockOutLoading, setClockOutLoading] = useState(false);
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const employeeDropdownRef = useRef<HTMLDivElement>(null);
  useOutsideClick({ ref: employeeDropdownRef, handler: () => setEmployeeDropdownOpen(false) });

  const dateRange = useMemo(() => {
    const range = getReportDateRange(
      datePreset,
      customFrom ? new Date(customFrom) : undefined,
      customTo ? new Date(customTo) : undefined
    );
    return { from: range.from.slice(0, 10), to: range.to.slice(0, 10) };
  }, [datePreset, customFrom, customTo]);

  const employeeMap = useMemo(() => {
    const m = new Map<string, string>();
    employees.forEach((e) => {
      const name = [e.firstName, e.lastName].filter(Boolean).join(" ") || e.email || e.correlationId;
      m.set(e.correlationId, name);
    });
    return m;
  }, [employees]);

  const fetchEmployees = useCallback(async () => {
    if (!storeCorrelationId) return;
    try {
      const { data } = await axBe.post<{
        data?: { storeEmployeesByStoreCorrelationId?: EmployeeOption[] };
      }>(GRAPHQL_ENDPOINT, {
        query: GET_EMPLOYEE,
        variables: { storeCorrelationId },
      });
      setEmployees(data?.data?.storeEmployeesByStoreCorrelationId ?? []);
    } catch {
      setEmployees([]);
    }
  }, [storeCorrelationId, axBe]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const fetchSessions = useCallback(
    async (cursor: { after?: string | null; before?: string | null }, pageWeAreLoading: number) => {
      if (!storeCorrelationId) return;
      setLoading(true);
      try {
        const variables: Record<string, unknown> = {
          input: {
            storeCorrelationId,
            fromDate: dateRange.from,
            toDate: dateRange.to,
          },
          first: cursor?.before != null ? undefined : PAGE_SIZE,
          last: cursor?.before != null ? PAGE_SIZE : undefined,
          after: cursor?.after ?? undefined,
          before: cursor?.before ?? undefined,
        };
        if (selectedEmployeeId !== "all") {
          (variables.input as Record<string, unknown>).userCorrelationIds = [selectedEmployeeId];
        }
        const { data } = await axBe.post<{
          data?: {
            getWorkSessions?: {
              pageInfo?: {
                hasNextPage?: boolean;
                hasPreviousPage?: boolean;
                startCursor?: string | null;
                endCursor?: string | null;
                totalNumberOfWorkSessions?: number;
              };
              edges?: Array<{ cursor?: string; node?: WorkSessionNode }>;
            };
          };
          errors?: Array<{ message?: string }>;
        }>(GRAPHQL_ENDPOINT, {
          query: GET_WORK_SESSIONS,
          variables,
        });
        const err = data?.errors?.[0];
        if (err) {
          failureToast(err.message ?? "Failed to load timecards");
          setSessions([]);
          setPageInfo({ hasNextPage: false, hasPreviousPage: false, startCursor: null, endCursor: null });
        } else {
          const conn = data?.data?.getWorkSessions;
          const edges = conn?.edges ?? [];
          setSessions(edges.map((e) => e.node).filter(Boolean) as WorkSessionNode[]);
          const pi = conn?.pageInfo ?? {};
          const endCursor = pi.endCursor ?? null;
          setPageInfo({
            hasNextPage: pi.hasNextPage ?? false,
            hasPreviousPage: pi.hasPreviousPage ?? false,
            startCursor: pi.startCursor ?? null,
            endCursor,
            totalNumberOfWorkSessions: pi.totalNumberOfWorkSessions,
          });
          if (endCursor != null) {
            setCursorCache((prev) => ({ ...prev, [pageWeAreLoading + 1]: endCursor }));
          }
        }
      } catch (e) {
        failureToast("Failed to load timecards");
        setSessions([]);
      } finally {
        setLoading(false);
      }
    },
    [storeCorrelationId, dateRange, selectedEmployeeId, axBe]
  );

  useEffect(() => {
    setAfter(null);
    setBefore(null);
    setCurrentPage(1);
    setCursorCache({});
  }, [dateRange, selectedEmployeeId]);

  useEffect(() => {
    if (!storeCorrelationId) return;
    fetchSessions({ after, before }, currentPage);
  }, [storeCorrelationId, dateRange, selectedEmployeeId, after, before, currentPage, fetchSessions]);

  const totalNumberOfSessions = pageInfo.totalNumberOfWorkSessions ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalNumberOfSessions / PAGE_SIZE));
  const start = totalNumberOfSessions === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const end = totalNumberOfSessions === 0 ? 0 : Math.min(currentPage * PAGE_SIZE, totalNumberOfSessions);
  const pageNumbers = getPaginationPages(currentPage, totalPages);

  const loadNextPage = useCallback(() => {
    if (loading) return;
    if (pageInfo.hasNextPage && pageInfo.endCursor) {
      setBefore(null);
      setAfter(pageInfo.endCursor);
      setCurrentPage((p) => p + 1);
    }
  }, [loading, pageInfo.hasNextPage, pageInfo.endCursor]);

  const loadPrevPage = useCallback(() => {
    if (loading) return;
    if (pageInfo.hasPreviousPage && pageInfo.startCursor) {
      setAfter(null);
      setBefore(pageInfo.startCursor);
      setCurrentPage((p) => Math.max(1, p - 1));
    }
  }, [loading, pageInfo.hasPreviousPage, pageInfo.startCursor]);

  const goToPage = useCallback(
    (page: number) => {
      if (loading) return;
      const target = Math.max(1, Math.min(page, totalPages));
      if (target === currentPage) return;
      if (target === 1) {
        setAfter(null);
        setBefore(null);
        setCurrentPage(1);
        return;
      }
      const cursor = cursorCache[target];
      if (cursor != null) {
        setAfter(cursor);
        setBefore(null);
        setCurrentPage(target);
      } else if (target === currentPage + 1 && pageInfo.endCursor) {
        loadNextPage();
      }
    },
    [loading, cursorCache, currentPage, totalPages, pageInfo.endCursor, loadNextPage]
  );

  const canGoToPage = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages) return false;
      if (page === 1) return true;
      if (page === currentPage + 1) return pageInfo.hasNextPage ?? false;
      return cursorCache[page] != null;
    },
    [totalPages, currentPage, cursorCache, pageInfo.hasNextPage]
  );

  const handleDateRangeChange = useCallback((preset: ReportDatePreset, from: string, to: string) => {
    setDatePreset(preset);
    setCustomFrom(preset === "custom" ? from : null);
    setCustomTo(preset === "custom" ? to : null);
  }, []);

  const handleClockOutForEmployee = async (notes: string) => {
    if (!storeCorrelationId || !clockOutTarget) return;
    setClockOutLoading(true);
    try {
      const { data } = await axBe.post<{
        data?: { employeeClockOut?: unknown };
        errors?: Array<{ message?: string }>;
      }>(GRAPHQL_ENDPOINT, {
        query: EMPLOYEE_CLOCK_OUT,
        variables: {
          workSessionInput: {
            storeCorrelationId,
            userCorrelationId: clockOutTarget.userCorrelationId,
            notes: notes || undefined,
            isManual: true,
          },
        },
      });
      const err = data?.errors?.[0];
      if (err) {
        failureToast(err.message ?? "Clock out failed");
      } else {
        successToast(`${clockOutTarget.employeeName} clocked out`);
        setClockOutTarget(null);
        fetchSessions({ after, before }, currentPage);
      }
    } catch (e) {
      failureToast("Clock out failed");
    } finally {
      setClockOutLoading(false);
    }
  };

  const summary = useMemo(() => {
    let totalHours = 0;
    let activeCount = 0;
    sessions.forEach((node) => {
      if (node.clockOutAt == null) activeCount += 1;
      else {
        const h = hoursBetween(node.clockInAt, node.clockOutAt);
        if (h != null) totalHours += h;
      }
    });
    return { totalHours, activeCount };
  }, [sessions]);

  const employeeLabel =
    selectedEmployeeId === "all" ? "All employees" : (employeeMap.get(selectedEmployeeId) ?? "All employees");

  if (!storeCorrelationId) {
    return (
      <div className="rounded-xl bg-white p-6 shadow-sm border border-[#e5e7eb]">
        <p className="text-gray-500 text-sm">Select a restaurant to view timecards.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 rounded-lg border border-[#e5e7eb] bg-white overflow-hidden">
      <div className="shrink-0 px-4 py-2.5 border-b border-[#e5e7eb] flex flex-wrap items-center justify-between gap-x-4 gap-y-2 min-h-0">
        <div className="flex items-center gap-2">
          <ReportDateRangePicker
            value={datePreset}
            onChange={handleDateRangeChange}
            customFrom={customFrom}
            customTo={customTo}
          />
          <div className="relative text-left" ref={employeeDropdownRef}>
          <button
            type="button"
            className="inline-flex w-full min-w-[150px] items-center justify-center gap-x-1.5 whitespace-nowrap rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#111827] hover:border-[#d1d5db] hover:bg-[#fafafa] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 focus-visible:border-[#FD7657] disabled:opacity-50 transition-colors"
            aria-expanded={employeeDropdownOpen}
            aria-haspopup="true"
            aria-label="Filter by employee"
            onClick={() => setEmployeeDropdownOpen((o) => !o)}
          >
            {employeeLabel}
            <svg
              className="-mr-1 h-5 w-5 text-[#9ca3af]"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          {employeeDropdownOpen && (
            <div
              className="absolute right-0 z-[100] mt-2 w-56 origin-top-right rounded-md border border-[#E5E7EB] bg-white shadow-lg focus:outline-none"
              role="menu"
              aria-orientation="vertical"
            >
              <div className="py-1" role="none">
                <button
                  type="button"
                  className={`block w-full px-4 py-2 text-left text-sm ${
                    selectedEmployeeId === "all" ? "bg-[#fef3ef] font-medium text-[#111827]" : "text-[#374151] hover:bg-[#f9fafb]"
                  }`}
                  role="menuitem"
                  onClick={() => {
                    setSelectedEmployeeId("all");
                    setEmployeeDropdownOpen(false);
                  }}
                >
                  All employees
                </button>
                {employees.map((e) => {
                  const name = [e.firstName, e.lastName].filter(Boolean).join(" ") || e.email || e.correlationId;
                  const isSelected = selectedEmployeeId === e.correlationId;
                  return (
                    <button
                      key={e.correlationId}
                      type="button"
                      className={`block w-full px-4 py-2 text-left text-sm ${
                        isSelected ? "bg-[#fef3ef] font-medium text-[#111827]" : "text-[#374151] hover:bg-[#f9fafb]"
                      }`}
                      role="menuitem"
                      onClick={() => {
                        setSelectedEmployeeId(e.correlationId);
                        setEmployeeDropdownOpen(false);
                      }}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        </div>
        <div className="text-sm text-gray-600 shrink-0">
          Total: <strong className="text-gray-900">{formatHours(summary.totalHours)}</strong>
          {summary.activeCount > 0 && (
            <>
              <span className="mx-2 text-[#d1d5db]" aria-hidden>•</span>
              Active: <strong className="text-[#FD7657]">{summary.activeCount}</strong>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">Loading…</div>
        ) : sessions.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">No timecards in this range.</div>
        ) : (
          <>
            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full text-left border-collapse" style={{ minWidth: 760 }}>
                <thead>
                  <tr className="sticky top-0 z-10 bg-[#f1f5f9] text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0] shadow-[0_1px_0_0_#e2e8f0]">
                    <th className="py-2.5 pl-4 pr-2 align-middle whitespace-nowrap">Date</th>
                    <th className="py-2.5 px-2 align-middle whitespace-nowrap">Employee</th>
                    <th className="py-2.5 px-2 align-middle whitespace-nowrap text-right">Clock in</th>
                    <th className="py-2.5 px-2 align-middle whitespace-nowrap text-right">Clock out</th>
                    <th className="py-2.5 px-2 align-middle whitespace-nowrap text-right">Hours</th>
                    <th className="py-2.5 px-2 align-middle whitespace-nowrap">Created by</th>
                    <th className="py-2.5 px-2 align-middle whitespace-nowrap">Updated by</th>
                    <th className="py-2.5 pl-2 pr-4 align-middle whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                {sessions.map((node, idx) => {
                  const isActive = node.clockOutAt == null;
                  const name = node.userCorrelationId ? employeeMap.get(node.userCorrelationId) ?? node.userCorrelationId : "—";
                  const hours = hoursBetween(node.clockInAt, node.clockOutAt);
                  return (
                    <tr
                      key={node.id != null ? String(node.id) : idx}
                      className={`border-b border-[#e5e7eb] hover:bg-[#f8fafc] ${isActive ? "bg-amber-50/60" : ""}`}
                    >
                      <td className="py-2.5 pl-4 pr-2 text-sm text-gray-900">{formatDate(node.clockInAt)}</td>
                      <td className="py-2.5 px-2 text-sm text-gray-700">{name}</td>
                      <td className="py-2.5 px-2 text-sm text-gray-700 text-right tabular-nums">{formatTime(node.clockInAt)}</td>
                      <td className="py-2.5 px-2 text-sm text-gray-700 text-right tabular-nums">{formatTime(node.clockOutAt)}</td>
                      <td className="py-2.5 px-2 text-sm text-gray-700 text-right tabular-nums">
                        {isActive ? (
                          <span className="inline-flex items-center justify-end gap-1.5">
                            <span>—</span>
                            <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FD7657]" aria-hidden />
                              Active
                            </span>
                          </span>
                        ) : (
                          formatHours(hours)
                        )}
                      </td>
                      <td className="py-2.5 px-2 text-sm text-gray-600">
                        {resolveCreatedByUpdatedBy(node.createdBy, employeeMap)}
                      </td>
                      <td className="py-2.5 px-2 text-sm text-gray-600">
                        {resolveCreatedByUpdatedBy(node.updatedBy, employeeMap)}
                      </td>
                      <td className="py-2.5 pl-2 pr-4">
                        {isActive && (
                          <button
                            type="button"
                            onClick={() => setClockOutTarget({ userCorrelationId: node.userCorrelationId ?? "", employeeName: name })}
                            disabled={clockOutLoading}
                            className="text-sm font-medium text-[#FD7657] hover:text-[#e66a4a] rounded-md border border-[#FD7657] px-2 py-1 hover:bg-[#fef3ef] disabled:opacity-50 transition-colors"
                          >
                            Clock out
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                </tbody>
              </table>
            </div>
            <section
              className="shrink-0 py-3.5 px-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-t bg-[#fafafa] relative"
              style={{ borderColor: "rgba(0,0,0,0.06)" }}
              aria-label="Pagination"
            >
              <p className="text-sm text-gray-500 order-2 sm:order-1 sm:flex-1 sm:min-w-0 sm:text-left">
                {totalNumberOfSessions > 0
                  ? `Showing ${start}–${end} of ${totalNumberOfSessions}`
                  : ""}
              </p>
              <div className="flex items-center justify-center gap-2 order-1 sm:order-2 sm:flex-none sm:absolute sm:left-1/2 sm:-translate-x-1/2">
                <button
                  type="button"
                  className="h-9 min-w-[56px] w-14 flex items-center justify-center text-sm font-medium text-[#252525] bg-white border border-[#F1F1F2] rounded-lg disabled:opacity-50 disabled:cursor-default focus:outline-none hover:bg-[#fafafa]"
                  onClick={loadPrevPage}
                  disabled={currentPage <= 1 || loading}
                >
                  Prev
                </button>
                <div className="flex items-center gap-1">
                  {pageNumbers.map((item, idx) =>
                    item === "ellipsis" ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-2 text-gray-400 text-sm"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={item}
                        type="button"
                        className={`min-w-[36px] w-9 h-9 rounded-lg text-sm font-medium flex items-center justify-center focus:outline-none ${
                          currentPage === item
                            ? "bg-orange text-white"
                            : "bg-gray-50 text-gray-700"
                        } disabled:opacity-50 disabled:cursor-default`}
                        onClick={() => goToPage(item)}
                        disabled={!canGoToPage(item)}
                      >
                        {item}
                      </button>
                    )
                  )}
                </div>
                <button
                  type="button"
                  className="h-9 min-w-[56px] w-14 flex items-center justify-center text-sm font-medium text-[#252525] bg-white border border-[#F1F1F2] rounded-lg disabled:opacity-50 disabled:cursor-default focus:outline-none hover:bg-[#fafafa]"
                  onClick={loadNextPage}
                  disabled={currentPage >= totalPages || loading}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:block flex-1 min-w-0 order-3" aria-hidden="true" />
            </section>
          </>
        )}
      </div>

      {clockOutTarget && (
        <ClockOutConfirmModal
          employeeName={clockOutTarget.employeeName}
          onConfirm={handleClockOutForEmployee}
          onCancel={() => setClockOutTarget(null)}
        />
      )}
    </div>
  );
}
